import { useEffect, useMemo, useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  MessageSquareText, Sparkles, Plus, Trash2, Search, X, Eye, Loader2,
  Building2, CheckCircle2, Clock, XCircle, RefreshCw, Wand2,
} from 'lucide-react';
import HelpTip from '@/components/HelpTip';
import GupshupFlowsPanel from '@/pages/backoffice/GupshupFlowsPanel';

const selectCls = 'w-full mt-1 bg-black/30 border border-white/10 rounded-md px-2 py-2 text-sm text-white';
const areaCls = 'w-full mt-1 bg-black/30 border border-white/10 rounded-md px-3 py-2 text-sm text-white min-h-[90px]';

const CATEGORIES = [
  { id: 'UTILITY', label: 'Utilidad (avisos, recordatorios)' },
  { id: 'MARKETING', label: 'Marketing (promociones)' },
  { id: 'AUTHENTICATION', label: 'Autenticación (códigos OTP)' },
];
const LANGS = [{ id: 'es', label: 'Español' }, { id: 'en', label: 'Inglés' }, { id: 'pt', label: 'Portugués' }];
const STATUS_FILTERS = [
  { id: 'all', label: 'Todas' },
  { id: 'APPROVED', label: 'Aprobadas' },
  { id: 'PENDING', label: 'En revisión' },
  { id: 'REJECTED', label: 'Rechazadas' },
];
const BTN_TYPES = [
  { id: 'QUICK_REPLY', label: 'Respuesta rápida' },
  { id: 'URL', label: 'Enlace (URL)' },
  { id: 'PHONE_NUMBER', label: 'Llamar (teléfono)' },
];

function StatusBadge({ status }) {
  const s = (status || '').toUpperCase();
  if (s === 'APPROVED') return <Badge className="bg-emerald-500/20 text-emerald-300 border-0 gap-1"><CheckCircle2 className="w-3 h-3" />Aprobada</Badge>;
  if (s === 'PENDING') return <Badge className="bg-amber-500/20 text-amber-300 border-0 gap-1"><Clock className="w-3 h-3" />En revisión</Badge>;
  if (s === 'REJECTED') return <Badge className="bg-red-500/20 text-red-300 border-0 gap-1"><XCircle className="w-3 h-3" />Rechazada</Badge>;
  return <Badge className="bg-zinc-700 text-zinc-300 border-0">{s || '—'}</Badge>;
}

function CatBadge({ category }) {
  const c = (category || '').toUpperCase();
  const map = { UTILITY: 'bg-sky-500/15 text-sky-300', MARKETING: 'bg-fuchsia-500/15 text-fuchsia-300', AUTHENTICATION: 'bg-indigo-500/15 text-indigo-300' };
  const es = { UTILITY: 'Utilidad', MARKETING: 'Marketing', AUTHENTICATION: 'Autenticación' };
  return <Badge className={`${map[c] || 'bg-zinc-700 text-zinc-300'} border-0`}>{es[c] || c}</Badge>;
}

const USAGE_COLOR = { automation: 'bg-fuchsia-500/15 text-fuchsia-300', campaign: 'bg-amber-500/15 text-amber-300', notification: 'bg-sky-500/15 text-sky-300' };

const EMPTY_FORM = { title: '', header: '', content: '', footer: '', example: '', category: 'UTILITY', language: 'es', buttons: [] };

export default function GupshupPlantillasFlows() {
  const [apps, setApps] = useState(null);
  const [appId, setAppId] = useState('');
  const [tpls, setTpls] = useState(null);           // {templates, approved, pending, rejected}
  const [loadingTpls, setLoadingTpls] = useState(false);
  const [filter, setFilter] = useState('all');
  const [q, setQ] = useState('');
  const [tab, setTab] = useState('plantillas');   // plantillas | flows

  const [creator, setCreator] = useState(false);    // modal abierto
  const [mode, setMode] = useState('ai');           // ai | manual
  const [brief, setBrief] = useState('');
  const [aiBusy, setAiBusy] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [variables, setVariables] = useState([]);   // hints de la IA
  const [saving, setSaving] = useState(false);

  const [view, setView] = useState(null);           // plantilla en detalle
  const [usage, setUsage] = useState({});           // element_name -> {module_type, usages[]}

  const loadApps = async () => {
    try {
      const { data } = await api.get('/admin/platform/gupshup/apps', { params: { only_live: true } });
      const list = (data?.apps || []).filter((a) => a.live && a.phone);
      setApps(list);
      if (list.length && !appId) {
        const lead = list.find((a) => /leadcem/i.test(a.name) || /leadcem/i.test(a.assigned?.name || ''));
        setAppId((lead || list[0]).id);
      }
    } catch (e) {
      setApps([]);
      toast.error(e.response?.data?.detail || 'No se pudo cargar los números (¿faltan credenciales Partner?)');
    }
  };

  const loadTpls = async (id) => {
    if (!id) return;
    setLoadingTpls(true); setTpls(null);
    try {
      const { data } = await api.get('/admin/platform/gupshup/templates', { params: { app_id: id } });
      setTpls(data);
      try {
        const u = await api.get('/admin/platform/gupshup/templates/usage', { params: { app_id: id } });
        setUsage(u.data?.by_template || {});
      } catch { setUsage({}); }
    } catch (e) {
      setTpls({ templates: [] });
      toast.error(e.response?.data?.detail || 'No se pudieron cargar las plantillas');
    } finally { setLoadingTpls(false); }
  };

  useEffect(() => { loadApps(); }, []);
  useEffect(() => { if (appId) loadTpls(appId); }, [appId]);

  const shown = useMemo(() => {
    let list = tpls?.templates || [];
    if (filter !== 'all') list = list.filter((t) => (t.status || '').toUpperCase() === filter);
    const s = q.trim().toLowerCase();
    if (s) list = list.filter((t) => (t.element_name || '').toLowerCase().includes(s) || (t.body || '').toLowerCase().includes(s));
    return list;
  }, [tpls, filter, q]);

  const selectedApp = (apps || []).find((a) => a.id === appId);

  const openCreator = (m) => {
    setMode(m); setBrief(''); setForm(EMPTY_FORM); setVariables([]); setCreator(true);
  };

  const generarIA = async () => {
    if (!brief.trim()) { toast.error('Describe qué quieres que diga la plantilla'); return; }
    setAiBusy(true);
    try {
      const { data } = await api.post('/admin/platform/gupshup/templates/ai', {
        app_id: appId, brief: brief.trim(), category: form.category, language: form.language,
      });
      const d = data?.draft || {};
      setForm((f) => ({
        ...f,
        title: d.title || '', header: d.header || '', content: d.content || '',
        footer: d.footer || '', example: d.example || '',
        buttons: Array.isArray(d.buttons) ? d.buttons.map((b) => ({
          type: (b.type || 'QUICK_REPLY').toUpperCase(), text: b.text || '', url: b.url || '', phone_number: b.phone_number || b.phone || '',
        })) : [],
        category: d.category || f.category, language: d.language || f.language,
      }));
      setVariables(Array.isArray(d.variables) ? d.variables : []);
      toast.success('Borrador listo. Revísalo y ajústalo antes de enviarlo.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'La IA no pudo generar la plantilla');
    } finally { setAiBusy(false); }
  };

  const addBtn = () => {
    if (form.buttons.length >= 3) { toast.info('Máximo 3 botones'); return; }
    setForm((f) => ({ ...f, buttons: [...f.buttons, { type: 'QUICK_REPLY', text: '', url: '', phone_number: '' }] }));
  };
  const updBtn = (i, patch) => setForm((f) => ({ ...f, buttons: f.buttons.map((b, k) => (k === i ? { ...b, ...patch } : b)) }));
  const delBtn = (i) => setForm((f) => ({ ...f, buttons: f.buttons.filter((_, k) => k !== i) }));

  const enviar = async () => {
    if (!appId) { toast.error('Elige un número'); return; }
    if (!form.content.trim()) { toast.error('El cuerpo del mensaje es obligatorio'); return; }
    setSaving(true);
    try {
      const payload = {
        app_id: appId, title: form.title, category: form.category, language: form.language,
        content: form.content, example: form.example || form.content,
        header: form.header, footer: form.footer,
        buttons: form.buttons.filter((b) => (b.text || '').trim()),
      };
      const { data } = await api.post('/admin/platform/gupshup/templates', payload);
      toast.success(`Plantilla enviada a revisión de Meta (${data.status || 'PENDING'})`);
      setCreator(false);
      loadTpls(appId);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo crear la plantilla');
    } finally { setSaving(false); }
  };

  const borrar = async (t) => {
    if (!window.confirm(`Vas a BORRAR la plantilla "${t.element_name}". Es irreversible. ¿Continuar?`)) return;
    try {
      await api.delete(`/admin/platform/gupshup/templates/${appId}/${encodeURIComponent(t.element_name)}`);
      toast.success('Plantilla borrada');
      loadTpls(appId);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo borrar');
    }
  };

  const setModule = async (t, mod) => {
    try {
      await api.post('/admin/platform/gupshup/templates/module', { app_id: appId, element_name: t.element_name, module_type: mod || null });
      setUsage((u) => ({ ...u, [t.element_name]: { ...(u[t.element_name] || { usages: [] }), module_type: mod || null } }));
      toast.success('Módulo actualizado');
    } catch (e) { toast.error(e.response?.data?.detail || 'No se pudo actualizar el módulo'); }
  };

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto">
      {/* Encabezado + tabs */}
      <div className="flex items-center gap-2 mb-1">
        <MessageSquareText className="w-6 h-6 text-emerald-400 shrink-0" />
        <h1 className="text-xl sm:text-2xl font-bold text-white">Plantillas y Flows</h1>
        <HelpTip title="¿Qué es esto?" content="Aquí ves y creas las plantillas de WhatsApp de cada número, directo desde LeadCEM (sin entrar al panel de Gupshup). Puedes crearlas con IA: describe qué quieres decir y la IA arma la plantilla. Meta las revisa (~24-48h) antes de poder usarlas." />
      </div>
      <p className="text-sm text-zinc-400 mb-4">Crea y administra las plantillas de WhatsApp de tus números. Todo desde aquí, sin entrar a Gupshup.</p>

      <div className="flex gap-2 mb-4 border-b border-white/10">
        <button onClick={() => setTab('plantillas')} data-testid="tab-plantillas"
          className={tab === 'plantillas' ? 'px-3 py-2 text-sm font-semibold text-emerald-300 border-b-2 border-emerald-400' : 'px-3 py-2 text-sm text-zinc-400 hover:text-zinc-200'}>Plantillas</button>
        <button onClick={() => setTab('flows')} data-testid="tab-flows"
          className={tab === 'flows' ? 'px-3 py-2 text-sm font-semibold text-emerald-300 border-b-2 border-emerald-400' : 'px-3 py-2 text-sm text-zinc-400 hover:text-zinc-200'}>Flows</button>
      </div>

      {/* Selector de número/app */}
      <Card className="bg-zinc-900/60 border-white/10 mb-4">
        <CardContent className="p-4">
          <Label className="text-xs text-zinc-400 flex items-center gap-1">
            <Building2 className="w-3.5 h-3.5" /> Número / cuenta de WhatsApp
            <HelpTip title="¿Qué número es este?" content="Cada plantilla vive dentro de un número de WhatsApp (una app de Gupshup). Elige el número del que quieres ver o crear plantillas." />
          </Label>
          {apps === null ? (
            <div className="mt-2 text-sm text-zinc-400 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Cargando números…</div>
          ) : apps.length === 0 ? (
            <div className="mt-2 text-sm text-amber-300">No hay números activos de WhatsApp conectados.</div>
          ) : (
            <select className={selectCls} value={appId} onChange={(e) => setAppId(e.target.value)} data-testid="app-picker">
              {apps.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name} · {a.phone}{a.assigned?.name ? ` · ${a.assigned.name}` : ''}
                </option>
              ))}
            </select>
          )}
        </CardContent>
      </Card>

      {tab === 'flows' && <GupshupFlowsPanel appId={appId} selectedApp={selectedApp} />}

      {tab === 'plantillas' && (<>
      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row gap-2 sm:items-center mb-3">
        <div className="flex gap-2">
          <Button onClick={() => openCreator('ai')} className="bg-emerald-600 hover:bg-emerald-500 gap-1" disabled={!appId} data-testid="btn-crear-ia">
            <Sparkles className="w-4 h-4" /> Crear con IA
          </Button>
          <Button onClick={() => openCreator('manual')} variant="outline" className="border-white/15 gap-1" disabled={!appId}>
            <Plus className="w-4 h-4" /> Manual
          </Button>
          <Button onClick={() => loadTpls(appId)} variant="ghost" className="text-zinc-400" disabled={!appId} title="Recargar">
            <RefreshCw className={`w-4 h-4 ${loadingTpls ? 'animate-spin' : ''}`} />
          </Button>
        </div>
        <div className="relative sm:ml-auto sm:w-64">
          <Search className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 text-zinc-500" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar plantilla…" className="pl-8 bg-black/30 border-white/10" />
        </div>
      </div>

      {/* Filtros de estado */}
      <div className="flex flex-wrap gap-2 mb-4">
        {STATUS_FILTERS.map((f) => (
          <button key={f.id} onClick={() => setFilter(f.id)}
            className={`px-3 py-1 rounded-full text-xs border ${filter === f.id ? 'bg-emerald-500/20 text-emerald-200 border-emerald-500/40' : 'bg-black/20 text-zinc-400 border-white/10'}`}>
            {f.label}
            {f.id === 'APPROVED' && tpls ? ` (${tpls.approved || 0})` : ''}
            {f.id === 'PENDING' && tpls ? ` (${tpls.pending || 0})` : ''}
            {f.id === 'REJECTED' && tpls ? ` (${tpls.rejected || 0})` : ''}
          </button>
        ))}
      </div>

      {/* Lista */}
      {loadingTpls ? (
        <div className="text-sm text-zinc-400 flex items-center gap-2 py-8 justify-center"><Loader2 className="w-4 h-4 animate-spin" /> Cargando plantillas…</div>
      ) : !appId ? (
        <div className="text-sm text-zinc-500 py-8 text-center">Elige un número para ver sus plantillas.</div>
      ) : shown.length === 0 ? (
        <div className="text-sm text-zinc-500 py-10 text-center border border-dashed border-white/10 rounded-lg">
          {(tpls?.templates || []).length === 0 ? 'Este número aún no tiene plantillas. Crea la primera con IA.' : 'Ninguna plantilla coincide con el filtro.'}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {shown.map((t) => (
            <Card key={t.id || t.element_name} className="bg-zinc-900/60 border-white/10 flex flex-col">
              <CardContent className="p-4 flex flex-col gap-2 h-full">
                <div className="flex flex-wrap items-center gap-1.5">
                  <StatusBadge status={t.status} />
                  <CatBadge category={t.category} />
                  <span className="text-[10px] uppercase text-zinc-500 ml-auto">{t.language}</span>
                </div>
                <div className="text-[11px] font-mono text-zinc-500 truncate" title={t.element_name}>{t.element_name}</div>
                {t.header ? <div className="text-sm font-semibold text-white">{t.header}</div> : null}
                <div className="text-sm text-zinc-300 whitespace-pre-wrap break-words line-clamp-4">{t.body}</div>
                {t.footer ? <div className="text-xs text-zinc-500">{t.footer}</div> : null}
                {Array.isArray(t.buttons) && t.buttons.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-1">
                    {t.buttons.map((b, i) => (
                      <span key={i} className="text-[11px] px-2 py-0.5 rounded bg-sky-500/10 text-sky-300 border border-sky-500/20">{b.text || b.type}</span>
                    ))}
                  </div>
                )}
                {t.status === 'REJECTED' && t.reason ? <div className="text-[11px] text-red-300">Motivo: {t.reason}</div> : null}
                <div className="border-t border-white/5 pt-2 mt-1 space-y-1.5">
                  <div className="flex flex-wrap items-center gap-1">
                    <span className="text-[10px] text-zinc-500">Usado en:</span>
                    {(usage[t.element_name]?.usages || []).length === 0
                      ? <span className="text-[10px] text-zinc-600">sin usar aún</span>
                      : usage[t.element_name].usages.map((u, i) => (
                          <span key={i} className={`text-[10px] px-1.5 py-0.5 rounded ${USAGE_COLOR[u.type] || 'bg-zinc-700 text-zinc-300'}`} title={u.detail || ''}>{u.name}</span>
                        ))}
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="text-[10px] text-zinc-500">Módulo:</span>
                    <select value={usage[t.element_name]?.module_type || ''} onChange={(e) => setModule(t, e.target.value)}
                      className="bg-black/30 border border-white/10 rounded px-1.5 py-0.5 text-[11px] text-white">
                      <option value="">— sin asignar —</option>
                      <option value="notificaciones">Notificaciones</option>
                      <option value="logicas">Automatizaciones</option>
                      <option value="campanas">Campañas</option>
                    </select>
                    <HelpTip title="¿Para qué sirve el módulo?" content="Marca en qué módulo debe aparecer/sugerirse esta plantilla al elegirla (Notificaciones, Automatizaciones o Campañas). 'Usado en' te dice dónde está conectada de verdad hoy." />
                  </div>
                </div>
                <div className="flex gap-2 mt-auto pt-2">
                  <Button size="sm" variant="ghost" className="text-zinc-300 h-8 px-2" onClick={() => setView(t)}><Eye className="w-4 h-4 mr-1" />Ver</Button>
                  <Button size="sm" variant="ghost" className="text-red-300 hover:text-red-200 h-8 px-2 ml-auto" onClick={() => borrar(t)}><Trash2 className="w-4 h-4" /></Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* ── Modal crear ─────────────────────────────────────────────── */}
      {creator && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 p-0 sm:p-4" onClick={() => !saving && !aiBusy && setCreator(false)}>
          <div className="bg-zinc-950 border border-white/10 rounded-t-2xl sm:rounded-2xl w-full sm:max-w-2xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-zinc-950 border-b border-white/10 px-4 py-3 flex items-center gap-2 z-10">
              <MessageSquareText className="w-5 h-5 text-emerald-400" />
              <h3 className="text-white font-semibold">Nueva plantilla</h3>
              <span className="text-xs text-zinc-500 truncate ml-1">· {selectedApp?.name} {selectedApp?.phone}</span>
              <Button variant="ghost" className="ml-auto text-zinc-400 h-8 w-8 p-0" onClick={() => !saving && !aiBusy && setCreator(false)}><X className="w-5 h-5" /></Button>
            </div>

            <div className="p-4 space-y-4">
              {/* sub-tabs ai/manual */}
              <div className="flex gap-2">
                <button onClick={() => setMode('ai')} className={`flex-1 px-3 py-2 rounded-lg text-sm border flex items-center justify-center gap-1 ${mode === 'ai' ? 'bg-emerald-500/15 text-emerald-200 border-emerald-500/40' : 'bg-black/20 text-zinc-400 border-white/10'}`}>
                  <Sparkles className="w-4 h-4" /> Con IA
                </button>
                <button onClick={() => setMode('manual')} className={`flex-1 px-3 py-2 rounded-lg text-sm border flex items-center justify-center gap-1 ${mode === 'manual' ? 'bg-emerald-500/15 text-emerald-200 border-emerald-500/40' : 'bg-black/20 text-zinc-400 border-white/10'}`}>
                  <Plus className="w-4 h-4" /> Manual
                </button>
              </div>

              {/* categoria + idioma (siempre) */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-zinc-400 flex items-center gap-1">Tipo
                    <HelpTip title="Tipo de plantilla" content="Utilidad: avisos y recordatorios (lo más común, se aprueba fácil). Marketing: promociones. Autenticación: códigos OTP. Meta rechaza las de Utilidad que parezcan promoción." />
                  </Label>
                  <select className={selectCls} value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}>
                    {CATEGORIES.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
                  </select>
                </div>
                <div>
                  <Label className="text-xs text-zinc-400">Idioma</Label>
                  <select className={selectCls} value={form.language} onChange={(e) => setForm((f) => ({ ...f, language: e.target.value }))}>
                    {LANGS.map((l) => <option key={l.id} value={l.id}>{l.label}</option>)}
                  </select>
                </div>
              </div>

              {mode === 'ai' && (
                <div className="rounded-lg border border-emerald-500/20 bg-emerald-950/20 p-3 space-y-2">
                  <Label className="text-xs text-emerald-300 flex items-center gap-1"><Wand2 className="w-3.5 h-3.5" /> Describe qué quieres que diga</Label>
                  <textarea className={areaCls} value={brief} onChange={(e) => setBrief(e.target.value)}
                    placeholder="Ej: Recordatorio de cita con el nombre del cliente, la fecha y la hora, y un botón para confirmar asistencia." />
                  <Button onClick={generarIA} disabled={aiBusy} className="bg-emerald-600 hover:bg-emerald-500 gap-1 w-full sm:w-auto">
                    {aiBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Generar con IA
                  </Button>
                  <p className="text-[11px] text-zinc-500">La IA arma la plantilla. Podrás revisarla y ajustarla abajo antes de enviarla.</p>
                </div>
              )}

              {/* Editor (compartido) */}
              <div className="space-y-3">
                <div>
                  <Label className="text-xs text-zinc-400">Nombre (para identificarla)</Label>
                  <Input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} placeholder="Recordatorio de cita" className="bg-black/30 border-white/10 mt-1" />
                </div>
                <div>
                  <Label className="text-xs text-zinc-400">Encabezado (opcional)</Label>
                  <Input value={form.header} onChange={(e) => setForm((f) => ({ ...f, header: e.target.value }))} maxLength={60} placeholder="Recordatorio de tu cita" className="bg-black/30 border-white/10 mt-1" />
                </div>
                <div>
                  <Label className="text-xs text-zinc-400 flex items-center gap-1">Cuerpo del mensaje *
                    <HelpTip title="Variables" content="Usa {{1}}, {{2}}… donde va un dato que cambia por cliente (nombre, fecha, etc.). El 'ejemplo' de abajo debe mostrar cómo se ve con datos reales." />
                  </Label>
                  <textarea className={areaCls} value={form.content} onChange={(e) => setForm((f) => ({ ...f, content: e.target.value }))}
                    placeholder="Hola {{1}}, te recordamos tu cita el {{2}} a las {{3}}." maxLength={1024} />
                </div>
                {variables.length > 0 && (
                  <div className="text-[11px] text-zinc-400 bg-black/20 rounded p-2 border border-white/5">
                    <span className="text-zinc-500">Variables:</span>{' '}
                    {variables.map((v) => `{{${v.index}}} = ${v.label || ''} (ej. ${v.example || ''})`).join('  ·  ')}
                  </div>
                )}
                <div>
                  <Label className="text-xs text-zinc-400 flex items-center gap-1">Ejemplo con datos reales
                    <HelpTip title="¿Por qué el ejemplo?" content="Meta pide ver cómo queda el mensaje con las variables rellenadas, para aprobarlo. Rellena {{1}}, {{2}}… con valores de muestra." />
                  </Label>
                  <textarea className={areaCls} value={form.example} onChange={(e) => setForm((f) => ({ ...f, example: e.target.value }))}
                    placeholder="Hola Juan, te recordamos tu cita el 15 de mayo a las 10:00 am." />
                </div>
                <div>
                  <Label className="text-xs text-zinc-400">Pie de página (opcional)</Label>
                  <Input value={form.footer} onChange={(e) => setForm((f) => ({ ...f, footer: e.target.value }))} maxLength={60} placeholder="LeadCEM" className="bg-black/30 border-white/10 mt-1" />
                </div>

                {/* Botones */}
                <div>
                  <div className="flex items-center gap-2">
                    <Label className="text-xs text-zinc-400">Botones (opcional, máx 3)</Label>
                    <Button size="sm" variant="ghost" className="text-emerald-300 h-7 px-2 ml-auto" onClick={addBtn}><Plus className="w-3.5 h-3.5 mr-1" />Agregar</Button>
                  </div>
                  <div className="space-y-2 mt-1">
                    {form.buttons.map((b, i) => (
                      <div key={i} className="flex flex-col sm:flex-row gap-2 bg-black/20 rounded-md p-2 border border-white/5">
                        <select className="bg-black/30 border border-white/10 rounded-md px-2 py-1.5 text-xs text-white sm:w-40" value={b.type} onChange={(e) => updBtn(i, { type: e.target.value })}>
                          {BTN_TYPES.map((bt) => <option key={bt.id} value={bt.id}>{bt.label}</option>)}
                        </select>
                        <Input value={b.text} onChange={(e) => updBtn(i, { text: e.target.value })} maxLength={25} placeholder="Texto del botón" className="bg-black/30 border-white/10 text-xs h-8 flex-1" />
                        {b.type === 'URL' && <Input value={b.url} onChange={(e) => updBtn(i, { url: e.target.value })} placeholder="https://…" className="bg-black/30 border-white/10 text-xs h-8 flex-1" />}
                        {b.type === 'PHONE_NUMBER' && <Input value={b.phone_number} onChange={(e) => updBtn(i, { phone_number: e.target.value })} placeholder="+52…" className="bg-black/30 border-white/10 text-xs h-8 flex-1" />}
                        <Button size="sm" variant="ghost" className="text-red-300 h-8 w-8 p-0" onClick={() => delBtn(i)}><Trash2 className="w-4 h-4" /></Button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="rounded-lg bg-amber-950/20 border border-amber-500/20 p-2.5 text-[11px] text-amber-200">
                Meta revisa cada plantilla nueva (normalmente 24-48 h). Aparecerá como <b>En revisión</b> hasta que la apruebe; después ya se puede usar.
              </div>
            </div>

            <div className="sticky bottom-0 bg-zinc-950 border-t border-white/10 px-4 py-3 flex gap-2 justify-end">
              <Button variant="outline" className="border-white/10" onClick={() => setCreator(false)} disabled={saving}>Cancelar</Button>
              <Button onClick={enviar} disabled={saving || !form.content.trim()} className="bg-emerald-600 hover:bg-emerald-500 gap-1">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Enviar a revisión de Meta
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal ver ───────────────────────────────────────────────── */}
      {view && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 p-0 sm:p-4" onClick={() => setView(null)}>
          <div className="bg-zinc-950 border border-white/10 rounded-t-2xl sm:rounded-2xl w-full sm:max-w-md max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="px-4 py-3 border-b border-white/10 flex items-center gap-2">
              <StatusBadge status={view.status} /><CatBadge category={view.category} />
              <Button variant="ghost" className="ml-auto text-zinc-400 h-8 w-8 p-0" onClick={() => setView(null)}><X className="w-5 h-5" /></Button>
            </div>
            <div className="p-4 space-y-2">
              <div className="text-[11px] font-mono text-zinc-500">{view.element_name} · {view.language}</div>
              {view.header ? <div className="text-sm font-semibold text-white">{view.header}</div> : null}
              <div className="text-sm text-zinc-200 whitespace-pre-wrap break-words">{view.body}</div>
              {view.footer ? <div className="text-xs text-zinc-500">{view.footer}</div> : null}
              {Array.isArray(view.buttons) && view.buttons.length > 0 && (
                <div className="flex flex-wrap gap-1 pt-1">
                  {view.buttons.map((b, i) => <span key={i} className="text-[11px] px-2 py-0.5 rounded bg-sky-500/10 text-sky-300 border border-sky-500/20">{b.text || b.type}</span>)}
                </div>
              )}
              {view.status === 'REJECTED' && view.reason ? <div className="text-[11px] text-red-300 pt-1">Motivo del rechazo: {view.reason}</div> : null}
            </div>
          </div>
        </div>
      )}
      </>)}
    </div>
  );
}
